import { useTranslation } from 'react-i18next';
import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { Upload, X, FileText, ImageIcon } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FileUpload = ({ 
  type = 'attachment', // 'avatar', 'logo', 'header-logo', 'attachment'
  entityType = null, // 'employee', 'customer', 'project'
  entityId = null,
  currentFile = null,
  onUploadComplete = () => {},
  onUploadError = () => {},
  accept = '*/*',
  maxSize = 10 * 1024 * 1024, // 10MB
  description = null,
  category = null,
  crewVisible = false,
  visibility = null,
}) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    // Check file size
    if (file.size > maxSize) {
      toast.error(`File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`);
      return false;
    }

    // Check file type for avatars, logos, header logos and favicons.
    // Favicons additionally allow .ico and .svg.
    if (type === 'avatar' || type === 'logo' || type === 'header-logo') {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(t('auto.misc2.onlyJpegPngGifAndWebp'));
        return false;
      }
    } else if (type === 'favicon') {
      const allowedTypes = ['image/x-icon', 'image/vnd.microsoft.icon', 'image/png', 'image/svg+xml', 'image/jpeg', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(t('auto.misc2.faviconMustBeIcoPngSvg'));
        return false;
      }
    }

    return true;
  };

  const uploadFile = async (file) => {
    if (!validateFile(file)) return;

    setUploading(true);
    setProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      let endpoint = '';
      
      if (type === 'avatar') {
        endpoint = `upload/avatar/${entityId}`;
      } else if (type === 'logo') {
        if (entityId === 'company') {
          endpoint = 'company/logo';
        } else {
          endpoint = `upload/logo/${entityId}`;
        }
      } else if (type === 'header-logo') {
        endpoint = 'company/header-logo';
      } else if (type === 'favicon') {
        endpoint = 'company/favicon';
      } else if (type === 'attachment') {
        endpoint = `upload/attachment?entity_type=${entityType}&entity_id=${entityId}`;
        if (description) {
          formData.append('description', description);
        }
        if (category) {
          formData.append('category', category);
        }
        if (crewVisible) {
          formData.append('crew_visible', 'true');
        }
        if (visibility) {
          formData.append('visible_to_all', visibility.all ? 'true' : 'false');
          if (!visibility.all) formData.append('visible_roles', (visibility.roles || []).join(','));
        }
      }

      const response = await axios.post(`${API}/${endpoint}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setProgress(progress);
        },
      });

      toast.success(response.data.message || 'File uploaded successfully');
      onUploadComplete(response.data);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Upload failed';
      toast.error(errorMessage);
      onUploadError(error);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      uploadFile(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const getAcceptAttribute = () => {
    if (type === 'avatar' || type === 'logo' || type === 'header-logo') {
      return 'image/jpeg,image/png,image/gif,image/webp';
    }
    if (type === 'favicon') {
      return 'image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml,image/jpeg,image/gif,image/webp,.ico,.svg';
    }
    return accept;
  };

  const renderCurrentFile = () => {
    if (!currentFile) return null;

    const isImage = type === 'avatar' || type === 'logo' || type === 'header-logo' || type === 'favicon';
    
    return (
      <div className="mb-4 p-4 border rounded-lg bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {isImage ? (
              <img 
                src={currentFile.startsWith('http') ? currentFile : `${BACKEND_URL}/api/files/${type}/${currentFile}`}
                alt={t('auto.misc2.currentFile')}
                className="w-12 h-12 object-cover rounded"
              />
            ) : (
              <FileText className="w-8 h-8 text-gray-500" />
            )}
            <div>
              <p className="text-sm font-medium">Current {type}</p>
              <p className="text-xs text-gray-500">{currentFile}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      {renderCurrentFile()}
      
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragOver
            ? 'border-blue-400 bg-blue-50'
            : uploading
            ? 'border-gray-300 bg-gray-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {uploading ? (
          <div className="space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm text-gray-600">{t('auto.misc2.uploading')}</p>
            <Progress value={progress} className="w-full" />
            <p className="text-xs text-gray-500">{progress}%</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-center">
              {type === 'avatar' || type === 'logo' || type === 'header-logo' || type === 'favicon' ? (
                <ImageIcon className="h-12 w-12 text-gray-400" />
              ) : (
                <Upload className="h-12 w-12 text-gray-400" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                Drop your {type} here, or click to browse
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {type === 'avatar' || type === 'logo' || type === 'header-logo' || type === 'favicon'
                  ? 'PNG, JPG, GIF, WebP up to 10MB'
                  : 'PDF, DOC, XLS, TXT, Images up to 10MB'
                }
              </p>
            </div>
            <Button onClick={openFileDialog} variant="outline" size="sm" data-testid="btn-openfiledialog">
              <Upload className="h-4 w-4 mr-2" />
              {t('auto.misc2.chooseFile')}
            </Button>
          </div>
        )}
      </div>

      <Input
        ref={fileInputRef}
        type="file"
        accept={getAcceptAttribute()}
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

export default FileUpload;